/**
 * Expert reviews.
 *
 * `carousel`, `framed`, `plain` and `cards` run Swiper; `grid` is static markup and needs no script. `carousel` is
 * the homepage band, a Swiper carousel at every width. Designer note (Figma 2082:58): infinite and drag-enabled with
 * autoplay off — the track only moves when the user drags or clicks the two
 * arrow buttons under the heading.
 *
 * `list` is /collections/ilana's band 07: a plain vertical list from 750px up,
 * and below it live's phone slideshow, which this element runs itself. The
 * stylesheet does all of the fading from the `is-selected` class; this only
 * moves that class, from the dots and from a horizontal swipe.
 */
class ExpertReviews extends HTMLElement {
  swiper = null;
  revealObserver = null;
  slideIndex = 0;
  swipeStart = null;

  constructor() {
    super();
    this.onWindowLoad = this.onWindowLoad.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.desktop = window.matchMedia('screen and (min-width: 750px)');
  }

  connectedCallback() {
    if (this.dataset.layout === 'list') {
      this.observeReveal();
      this.initSlideshow();
      return;
    }

    // theme.liquid loads Swiper with `defer`, so it is normally parsed before
    // this module runs. Fall back to the load event if it is not.
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', this.onWindowLoad, { once: true });
      return;
    }

    this.initSwiper();
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.onWindowLoad);
    this.revealObserver?.disconnect();
    this.audienceQuery?.removeEventListener('change', this.onAudienceChange);
    this.dotsElement?.removeEventListener('click', this.onDotClick);
    this.carousel?.removeEventListener('pointerdown', this.onPointerDown);
    this.carousel?.removeEventListener('pointerup', this.onPointerUp);
    this.carousel?.removeEventListener('pointercancel', this.onPointerCancel);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  get carousel() {
    return this.querySelector('.expert-reviews__carousel');
  }

  get dotsElement() {
    return this.querySelector('.expert-reviews__dots');
  }

  /**
   * Reveals each row once it is 200px into the viewport, the point at which
   * live's lazy portraits load and slide in. The stylesheet only hides the
   * portraits while `data-reveal` is present, so no observer means no hiding.
   */
  observeReveal() {
    if (!('IntersectionObserver' in window)) return;

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          entry.target.classList.add('is-revealed');
          this.revealObserver.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -200px 0px' },
    );

    for (const card of this.querySelectorAll('.expert-reviews__card')) {
      this.revealObserver.observe(card);
    }

    this.dataset.reveal = '';
  }

  onWindowLoad() {
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper || typeof Swiper === 'undefined' || !this.carousel) return;

    if (this.dataset.layout === 'framed') {
      this.initFramed();
      return;
    }

    if (this.dataset.layout === 'plain') {
      this.initPlain();
      return;
    }

    if (this.dataset.layout === 'cards') {
      this.initCards();
      return;
    }

    if (this.dataset.layout === 'grid') return;

    this.fillLoop();
    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 'auto',
      spaceBetween: 26,
      loop: true,
      grabCursor: true,
      watchOverflow: true,
      // piquelife.com's slick runs `adaptiveHeight` below 1080px: the band
      // eases to the current card's height rather than jumping when a longer
      // or shorter quote comes in. Swiper sizes the wrapper to the active slide
      // and transitions it over `speed`.
      autoHeight: true,
      // piquelife.com's slick moves one card in 300ms, linear — the easing is
      // set on the wrapper in section-expert-reviews.css.
      speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
      // The track's `touch-action: pan-y` (section-expert-reviews.css) lets the
      // browser scroll the page only for a drag steeper than 45deg, so Swiper
      // must take every flatter one. A smaller angle left a dead zone between
      // the two where a slanted thumb moved neither the cards nor the page.
      touchAngle: 45,
      // piquelife.com's slick changes card once a drag passes a fifth of the
      // track (touchThreshold 5: 79px on its 394px phone track). Swiper
      // measures against card plus gap (337px) and counts from the end of the
      // threshold below, so 0.2 (67px + 10px) lands on live's 79px; its
      // half-card default sprang a normal thumb swipe back.
      longSwipesRatio: 0.2,
      // Swiper picks sideways vs page scroll once, at the first 5px, and keeps
      // that for the whole touch; slick re-reads the direction as the finger
      // travels. A thumb that starts with a small downward wobble was read as a
      // page scroll and the swipe was ignored ("two or three tries" on
      // phones). Waiting 10px reads the direction from the real movement.
      threshold: 10,
      navigation: {
        prevEl: this.querySelector('[data-expert-reviews-prev]'),
        nextEl: this.querySelector('[data-expert-reviews-next]'),
      },
      // Swiper's a11y module overwrites the buttons' aria-label with these,
      // and its defaults are hardcoded English. See the section's comment.
      a11y: {
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
      },
      breakpoints: {
        750: { spaceBetween: 16, longSwipesRatio: 0.5 },
      },
      on: {
        // A swipe that starts while the last card is still moving is measured from the card's mid-move position, so
        // a normal thumb swipe landed on the card it was already heading to and the band seemed stuck (Homepage QA
        // row 13). Finish the move first, so the swipe counts from the new card as slick's does. `slideTo` would
        // return early here (the target is already `swiper.translate`), so the wrapper is set there directly.
        touchStart: (swiper) => {
          if (!swiper.animating) return;
          swiper.setTransition(0);
          swiper.setTranslate(swiper.translate);
          swiper.$wrapperEl.trigger('transitionend');
        },
      },
    });
  }

  /**
   * Swiper 7 duplicates at most one set of cards either side of a loop, so a short set (four experts on a product
   * page) runs out before the track's end and the next arrow stops on the last card. live's slick keeps going, so the
   * cards are repeated until one set is twice the track's width. The copies are hidden from assistive technology.
   */
  fillLoop() {
    const wrapper = this.carousel.querySelector('.swiper-wrapper');
    const cards = [...wrapper.children];
    if (!cards.length) return;

    const needed = this.carousel.offsetWidth * 2;
    const setWidth = () => [...wrapper.children].reduce((sum, card) => sum + card.offsetWidth, 0);

    while (setWidth() < needed && wrapper.children.length < cards.length * 8) {
      for (const card of cards) {
        const copy = card.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('data-shopify-editor-block');
        wrapper.append(copy);
      }
    }
  }

  /**
   * The landing pages' framed slider: slick's settings on piquelife.com are
   * infinite, one card a step, linear, 300ms on desktop, and from 640px down a
   * centred card with dots and a 1s step. There is no autoplay.
   */
  initFramed() {
    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      loop: true,
      grabCursor: true,
      speed: 1000,
      centeredSlides: true,
      navigation: {
        prevEl: this.querySelector('[data-expert-reviews-prev]'),
        nextEl: this.querySelector('[data-expert-reviews-next]'),
      },
      pagination: {
        el: this.querySelector('.expert-reviews__pagination'),
        clickable: true,
        bulletClass: 'expert-reviews__dot',
        bulletActiveClass: 'is-active',
      },
      a11y: {
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
      },
      breakpoints: {
        641: { centeredSlides: false, speed: 300 },
      },
    });

    // Swiper measures the slides before it marks which is active, and the active
    // (and, wide-card, next) card has its own width, so measure once more.
    this.swiper.update();

    // QA2: the phones' own previous / next pair under the track (`phone_arrows`) is a second set of buttons, which
    // Swiper's navigation (one element per side) does not drive.
    this.querySelector('[data-expert-reviews-phone-prev]')?.addEventListener('click', () => this.swiper.slidePrev());
    this.querySelector('[data-expert-reviews-phone-next]')?.addEventListener('click', () => this.swiper.slideNext());
  }

  /**
   * The landing pages' plain cards: slick's settings on piquelife.com are infinite, one card a step, linear and
   * 300ms at every width; below 640px a centred card with a peek. No autoplay, no dots.
   */
  initPlain() {
    this.applyAudience();
    const overlay = this.hasAttribute('data-phone-overlay');
    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      loop: true,
      grabCursor: true,
      // slick's adaptiveHeight only works one card at a time: below 640px the track is as tall as the current
      // card (or as the tallest, when the cards are equal height); from there up it is as tall as the tallest.
      autoHeight: !this.hasAttribute('data-equal-phone'),
      speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches || overlay ? 0 : 300,
      // Batch 19 QI 3: live's phone slider is a fade, not a drag: the stylesheet stacks the cards and times the fade.
      allowTouchMove: !overlay,
      centeredSlides: true,
      navigation: {
        prevEl: this.querySelector('[data-expert-reviews-prev]'),
        nextEl: this.querySelector('[data-expert-reviews-next]'),
      },
      a11y: {
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
      },
      breakpoints: {
        640: {
          centeredSlides: false,
          autoHeight: false,
          ...(overlay ? { speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300, allowTouchMove: true } : {}),
        },
      },
      // Batch 8 flow: the phone overlay look has dots (live's flickity page dots); nothing changes without the attribute
      ...(this.hasAttribute('data-phone-overlay')
        ? {
            pagination: {
              el: this.querySelector('.expert-reviews__overlay-dots'),
              clickable: true,
              bulletClass: 'expert-reviews__odot',
              bulletActiveClass: 'is-active',
            },
          }
        : {}),
    });

    // The current card has its own width once Swiper has marked it, so measure the slides once more.
    this.swiper.update();

    // Batch 19 QI 3: live's slick / flickity cards load every photograph at once, so the next card never arrives blank.
    for (const image of this.querySelectorAll('img[loading="lazy"]')) image.loading = 'eager';

    if (overlay) this.initPhoneFade();

    this.initLeadStatic();
  }

  /**
   * Batch 13 QI: live's Tim "Recommended by health experts" keeps the lead card still and swaps its content, while
   * the portraits beside it slide behind (slick, 200ms, linear). Opt in with `--expert-reviews-lead-static: 1` from the
   * page's stylesheet: a copy of the current card is laid over the spot the card rests in and refreshed on every change.
   */
  initLeadStatic() {
    const carousel = this.carousel;
    if (getComputedStyle(carousel).getPropertyValue('--expert-reviews-lead-static').trim() !== '1') return;

    const swiper = this.swiper;
    const host = document.createElement('div');
    host.className = 'expert-reviews__lead-static';
    host.setAttribute('aria-hidden', 'true');
    carousel.append(host);
    swiper.params.speed = 200;
    // live's slick band does not follow a mouse drag (arrows, and a finger on touch screens)
    swiper.params.simulateTouch = false;

    let box = null;
    const measure = () => {
      const active = swiper.slides[swiper.activeIndex];
      if (!active) return;
      const slide = active.getBoundingClientRect();
      const frame = carousel.getBoundingClientRect();
      // Mid-transition the slide is on its way: keep the resting box measured while the track was still.
      if (swiper.animating && box) return;
      box = { left: slide.left - frame.left, top: slide.top - frame.top };
    };
    const sync = () => {
      const active = swiper.slides[swiper.activeIndex];
      if (!active || !box) return;
      const copy = active.cloneNode(true);
      copy.removeAttribute('style');
      copy.removeAttribute('id');
      copy.classList.remove('swiper-slide-duplicate', 'swiper-slide-next', 'swiper-slide-prev');
      copy.classList.add('swiper-slide-active');
      host.style.insetInlineStart = `${box.left}px`;
      host.style.insetBlockStart = `${box.top}px`;
      host.replaceChildren(copy);
    };

    swiper.on('slideChange', sync);
    swiper.on('resize', () => {
      box = null;
      window.requestAnimationFrame(() => {
        measure();
        sync();
      });
    });
    window.requestAnimationFrame(() => {
      measure();
      sync();
    });
  }

  /**
   * Batch 19 QI 3: live's phone slider (Flickity with a fade) changes card by a click on a dot or a swipe of 15px or
   * more, and its fade timeline is in the stylesheet. Each `data-motion` frame after the first paint turns the
   * transitions on, so the first card shows without one.
   */
  initPhoneFade() {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.setAttribute('data-motion', '')));

    const carousel = this.carousel;
    const phone = window.matchMedia('screen and (max-width: 640px)');
    let start = null;
    carousel.addEventListener('pointerdown', (event) => {
      start = { x: event.clientX, y: event.clientY };
    });
    carousel.addEventListener('pointerup', (event) => {
      if (!start || !phone.matches) return;
      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      start = null;
      if (Math.abs(deltaX) < 15 || Math.abs(deltaX) < Math.abs(deltaY)) return;

      if (deltaX < 0) this.swiper?.slideNext();
      else this.swiper?.slidePrev();
    });
    carousel.addEventListener('pointercancel', () => {
      start = null;
    });
  }

  /**
   * Batch 13: a plain card can be limited to desktop (show on mobile off) or to phones (show on desktop off), because
   * live keeps a desktop slider and a separate phone one. Swiper must not count the cards that do not apply, so they
   * are taken out of the track here; the stylesheet already hides them before this runs. Without any limited card
   * nothing happens. Crossing 641px rebuilds the slider.
   */
  applyAudience() {
    const wrapper = this.carousel?.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    if (!this.allCards) {
      this.allCards = [...wrapper.children];
      if (!this.allCards.some((card) => card.dataset.audience)) return;

      this.audienceQuery = window.matchMedia('screen and (min-width: 641px)');
      this.onAudienceChange = () => {
        this.swiper?.destroy(true, true);
        this.swiper = null;
        this.initPlain();
      };
      this.audienceQuery.addEventListener('change', this.onAudienceChange);
    }

    if (!this.audienceQuery) return;

    const wide = this.audienceQuery.matches;
    wrapper.replaceChildren(
      ...this.allCards.filter((card) => !card.dataset.audience || (card.dataset.audience === 'desktop') === wide),
    );
  }

  /**
   * The landing pages' cards slider: slick's settings on piquelife.com are infinite, one card a step and 500ms at
   * every width, three cards from 1024px, two from 641px and one card with its neighbours peeking and dots below. No
   * autoplay.
   */
  initCards() {
    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 1,
      spaceBetween: 20,
      loop: true,
      grabCursor: true,
      speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 500,
      navigation: {
        prevEl: this.querySelector('[data-expert-reviews-prev]'),
        nextEl: this.querySelector('[data-expert-reviews-next]'),
      },
      pagination: {
        el: this.querySelector('.expert-reviews__pagination'),
        clickable: true,
        bulletClass: 'expert-reviews__dot',
        bulletActiveClass: 'is-active',
      },
      a11y: {
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
      },
      breakpoints: {
        641: { slidesPerView: 2, spaceBetween: 40 },
        1024: { slidesPerView: 3, spaceBetween: 40 },
      },
    });
  }

  initSlideshow() {
    this.slides = [...this.querySelectorAll('.expert-reviews__card--slide')];
    this.dots = [...this.querySelectorAll('.expert-reviews__dot')];
    if (this.slides.length < 2 || !this.carousel) return;

    this.dotsElement?.addEventListener('click', this.onDotClick);
    this.carousel.addEventListener('pointerdown', this.onPointerDown);
    this.carousel.addEventListener('pointerup', this.onPointerUp);
    this.carousel.addEventListener('pointercancel', this.onPointerCancel);
  }

  /**
   * Selects a slide, wrapping at either end as live's Flickity does.
   * @param {number} index
   */
  select(index) {
    const next = (index + this.slides.length) % this.slides.length;
    if (next === this.slideIndex) return;

    this.slides[this.slideIndex].classList.remove('is-selected');
    this.dots[this.slideIndex]?.removeAttribute('aria-current');
    this.slides[next].classList.add('is-selected');
    this.dots[next]?.setAttribute('aria-current', 'true');
    this.slideIndex = next;
  }

  onDotClick(event) {
    const dot = event.target.closest('.expert-reviews__dot');
    if (!dot) return;

    this.select(this.dots.indexOf(dot));
  }

  onPointerDown(event) {
    this.swipeStart = { x: event.clientX, y: event.clientY };
  }

  /** A mostly horizontal drag of 30px or more moves one slide that way. */
  onPointerUp(event) {
    if (!this.swipeStart || this.desktop.matches) return;

    const deltaX = event.clientX - this.swipeStart.x;
    const deltaY = event.clientY - this.swipeStart.y;
    this.swipeStart = null;
    if (Math.abs(deltaX) < 30 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    this.select(this.slideIndex + (deltaX < 0 ? 1 : -1));
  }

  onPointerCancel() {
    this.swipeStart = null;
  }
}

if (!customElements.get('expert-reviews')) {
  customElements.define('expert-reviews', ExpertReviews);
}

/**
 * Batch 6: the `slider` layout.
 *
 * Live's ee-reviews-health-experts carousel. The cards sit on one grid track that is translated, one card per step, clamped
 * at both ends (no loop, so the last card can slide to the left edge and leave the row empty after it). The arrows (beside
 * the heading from 750px, under the track on phones), the dots (phones), a drag of 40px or more and a click all move it.
 * From 750px the track starts at the left edge of its window; on phones the active card is centred, with its neighbours
 * peeking. The 0.4s ease is in the stylesheet.
 */
class ExpertSlider extends HTMLElement {
  index = 0;
  pointerX = null;
  resizeObserver = null;

  constructor() {
    super();
    this.onPrev = () => this.goTo(this.index - 1);
    this.onNext = () => this.goTo(this.index + 1);
    this.onDot = (event) => this.goTo(this.dots.indexOf(event.currentTarget));
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.phone = window.matchMedia('screen and (max-width: 749px)');
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-expert-slider-viewport]');
    this.track = this.querySelector('[data-expert-slider-track]');
    if (!this.viewport || !this.track) return;

    this.cards = [...this.track.children];
    this.prevButtons = [...this.querySelectorAll('[data-expert-slider-prev]')];
    this.nextButtons = [...this.querySelectorAll('[data-expert-slider-next]')];
    this.dots = [...this.querySelectorAll('[data-expert-slider-dot]')];

    for (const button of this.prevButtons) button.addEventListener('click', this.onPrev);
    for (const button of this.nextButtons) button.addEventListener('click', this.onNext);
    for (const dot of this.dots) dot.addEventListener('click', this.onDot);
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerCancel);

    this.resizeObserver = new ResizeObserver(() => this.position(true));
    this.resizeObserver.observe(this.viewport);

    this.update();
    this.position(true);
  }

  disconnectedCallback() {
    for (const button of this.prevButtons ?? []) button.removeEventListener('click', this.onPrev);
    for (const button of this.nextButtons ?? []) button.removeEventListener('click', this.onNext);
    for (const dot of this.dots ?? []) dot.removeEventListener('click', this.onDot);
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('pointerup', this.onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.onPointerCancel);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /** @param {number} index clamped to the first and last card */
  goTo(index) {
    const next = Math.max(0, Math.min(index, this.cards.length - 1));
    if (next === this.index) return;

    this.index = next;
    this.update();
    this.position(false);
  }

  /** The arrows that have nowhere to go are disabled; the dot of the active card is current. */
  update() {
    const last = this.cards.length - 1;
    for (const button of this.prevButtons) button.disabled = this.index <= 0;
    for (const button of this.nextButtons) button.disabled = this.index >= last;
    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
  }

  /** @param {boolean} instant skip the slide (first paint, a resize) */
  position(instant) {
    const card = this.cards[0];
    if (!card) return;

    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const width = card.getBoundingClientRect().width;
    const step = width + gap;
    let offset = 0;

    if (this.cards.length > 1) {
      offset = this.phone.matches
        ? this.viewport.clientWidth / 2 - (this.index * step + width / 2)
        : -step * this.index;
    }

    if (instant) this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    if (instant) {
      this.track.getBoundingClientRect();
      this.track.style.transition = '';
    }
  }

  /** @param {PointerEvent} event */
  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pointerX = event.clientX;
    this.viewport.classList.add('is-grabbing');
    this.viewport.setPointerCapture?.(event.pointerId);
  }

  /** @param {PointerEvent} event */
  onPointerUp(event) {
    this.viewport.classList.remove('is-grabbing');
    if (this.pointerX === null) return;

    const distance = event.clientX - this.pointerX;
    this.pointerX = null;
    if (Math.abs(distance) < 40) return;

    this.goTo(this.index + (distance < 0 ? 1 : -1));
  }

  onPointerCancel() {
    this.pointerX = null;
    this.viewport.classList.remove('is-grabbing');
  }
}

if (!customElements.get('expert-slider')) {
  customElements.define('expert-slider', ExpertSlider);
}
